import { cloneElement, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  label: ReactNode;
  /** Skip entirely — e.g. the collapsed-sidebar rail only needs this when collapsed. */
  disabled?: boolean;
  children: ReactElement;
}

/**
 * Minimal hover/focus tooltip, portaled to `<body>` so it escapes scroll/clip
 * ancestors — the collapsed sidebar rail lives inside an `overflow-y-auto` nav,
 * which would otherwise clip a plain absolutely-positioned bubble.
 *
 * Attaches directly to the child element (via cloneElement) rather than
 * wrapping it, so it never disturbs flex/grid layout of the element it labels.
 */
export function Tooltip({ label, disabled, children }: TooltipProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (disabled) setPos(null);
  }, [disabled]);

  const measure = (el: HTMLElement) => {
    if (disabled) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 10 });
  };

  const child = cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      children.props.onMouseEnter?.(e);
      measure(e.currentTarget);
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      children.props.onMouseLeave?.(e);
      setPos(null);
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      children.props.onFocus?.(e);
      measure(e.currentTarget);
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      children.props.onBlur?.(e);
      setPos(null);
    },
  });

  return (
    <>
      {child}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-md bg-ink-900 px-2.5 py-1.5 text-[12px] font-medium text-white shadow-popover ring-1 ring-white/10"
            style={{ top: pos.top, left: pos.left }}
          >
            {label}
          </span>,
          document.body,
        )}
    </>
  );
}
